import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { AdminLogin } from "../components/AdminLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDevices = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);

  const [formData, setFormData] = useState({
    category: "laptop",
    manufacturer: "",
    model: "",
    storage: "",
    basePrice: "",
    isActive: true,
  });

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDevices();
    }
  }, [filterCategory, isAuthenticated]);

  const checkAuth = () => {
    const token = localStorage.getItem("adminToken");
    const expiry = localStorage.getItem("adminTokenExpiry");
    
    if (token && expiry && Date.now() < parseInt(expiry)) {
      setIsAuthenticated(true);
    } else {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminTokenExpiry");
      setIsAuthenticated(false);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    };
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminTokenExpiry");
    setIsAuthenticated(false);
    toast.success("Erfolgreich abgemeldet");
  };

  const fetchDevices = async () => {
    setIsLoading(true);
    try {
      const url =
        filterCategory === "all"
          ? `${BACKEND_URL}/api/admin/devices?active_only=false`
          : `${BACKEND_URL}/api/admin/devices?category=${filterCategory}&active_only=false`;

      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        toast.error("Session abgelaufen. Bitte erneut einloggen.");
        handleLogout();
        return;
      }

      const data = await response.json();

      if (data.success) {
        setDevices(data.devices);
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
      toast.error("Fehler beim Laden der Geräte");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const url = editingDevice
        ? `${BACKEND_URL}/api/admin/devices/${editingDevice.id}`
        : `${BACKEND_URL}/api/admin/devices`;

      const method = editingDevice ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          basePrice: parseFloat(formData.basePrice),
        }),
      });

      if (response.status === 401) {
        toast.error("Session abgelaufen. Bitte erneut einloggen.");
        handleLogout();
        return;
      }

      const data = await response.json();

      if (data.success) {
        toast.success(editingDevice ? "Gerät aktualisiert" : "Gerät erstellt");
        setShowForm(false);
        setEditingDevice(null);
        resetForm();
        fetchDevices();
      } else {
        toast.error(data.detail || "Ein Fehler ist aufgetreten");
      }
    } catch (error) {
      console.error("Error saving device:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (device) => {
    setEditingDevice(device);
    setFormData({
      category: device.category,
      manufacturer: device.manufacturer,
      model: device.model,
      storage: device.storage,
      basePrice: device.basePrice.toString(),
      isActive: device.isActive,
    });
    setShowForm(true);
  };

  const handleDelete = async (deviceId) => {
    if (!window.confirm("Möchten Sie dieses Gerät wirklich deaktivieren?")) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/devices/${deviceId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        toast.error("Session abgelaufen. Bitte erneut einloggen.");
        handleLogout();
        return;
      }

      const data = await response.json();

      if (data.success) {
        toast.success("Gerät deaktiviert");
        fetchDevices();
      }
    } catch (error) {
      console.error("Error deleting device:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const resetForm = () => {
    setFormData({
      category: "laptop",
      manufacturer: "",
      model: "",
      storage: "",
      basePrice: "",
      isActive: true,
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingDevice(null);
    resetForm();
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-[#143e6e] mb-2">Geräteverwaltung</h1>
            <p className="text-gray-600">Verwalten Sie Geräte und Preise für den C2B-Ankauf</p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="border-red-600 text-red-600 hover:bg-red-50"
          >
            <Icons.LogOut className="mr-2" size={18} />
            Abmelden
          </Button>
        </div>

        {/* Header Actions */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2">
            <Button
              variant={filterCategory === "all" ? "default" : "outline"}
              onClick={() => setFilterCategory("all")}
              className={filterCategory === "all" ? "bg-[#1f7db6]" : ""}
            >
              Alle
            </Button>
            <Button
              variant={filterCategory === "laptop" ? "default" : "outline"}
              onClick={() => setFilterCategory("laptop")}
              className={filterCategory === "laptop" ? "bg-[#1f7db6]" : ""}
            >
              Laptops
            </Button>
            <Button
              variant={filterCategory === "smartphone" ? "default" : "outline"}
              onClick={() => setFilterCategory("smartphone")}
              className={filterCategory === "smartphone" ? "bg-[#1f7db6]" : ""}
            >
              Smartphones
            </Button>
          </div>

          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Icons.Plus className="mr-2" size={18} />
            Neues Gerät
          </Button>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{editingDevice ? "Gerät bearbeiten" : "Neues Gerät hinzufügen"}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Kategorie *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="laptop">Laptop</SelectItem>
                        <SelectItem value="smartphone">Smartphone</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Hersteller *</Label>
                    <Input
                      value={formData.manufacturer}
                      onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                      placeholder="z.B. Apple, Dell"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Modell *</Label>
                    <Input
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      placeholder="z.B. MacBook Air M3"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Speicher *</Label>
                    <Input
                      value={formData.storage}
                      onChange={(e) => setFormData({ ...formData, storage: e.target.value })}
                      placeholder="z.B. 256GB, 512GB"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Basispreis (€) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.basePrice}
                      onChange={(e) => setFormData({ ...formData, basePrice: e.target.value })}
                      placeholder="z.B. 750"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div className="flex items-center space-x-2 pt-6">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-[#1f7db6] focus:ring-[#1f7db6]"
                    />
                    <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                      Aktiv
                    </label>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" disabled={isLoading} className="bg-[#1f7db6] hover:bg-[#143e6e]">
                    {isLoading ? (
                      <>
                        <Icons.Loader2 className="mr-2 animate-spin" size={18} />
                        Speichern...
                      </>
                    ) : (
                      <>
                        <Icons.Save className="mr-2" size={18} />
                        {editingDevice ? "Aktualisieren" : "Erstellen"}
                      </>
                    )}
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCancel}>
                    Abbrechen
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Devices Table */}
        <Card>
          <CardHeader>
            <CardTitle>
              Geräte ({devices.length})
              {isLoading && <Icons.Loader2 className="inline ml-2 animate-spin" size={20} />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategorie</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hersteller</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modell</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Speicher</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Basispreis</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {devices.map((device) => (
                    <tr key={device.id} className={!device.isActive ? "opacity-50" : ""}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="capitalize">{device.category}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap font-medium">{device.manufacturer}</td>
                      <td className="px-4 py-4">{device.model}</td>
                      <td className="px-4 py-4 whitespace-nowrap">{device.storage}</td>
                      <td className="px-4 py-4 whitespace-nowrap font-semibold text-[#1f7db6]">
                        {device.basePrice.toFixed(2)}€
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {device.isActive ? (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Aktiv</span>
                        ) : (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">Inaktiv</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(device)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Icons.Edit size={16} />
                          </Button>
                          {device.isActive && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(device.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Icons.Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {devices.length === 0 && !isLoading && (
                <div className="text-center py-12 text-gray-500">
                  <Icons.Package size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Keine Geräte gefunden</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
